import { isAfter, isWithinInterval } from "date-fns";

import { parseDateAndTime } from "@/utils/datetime";

// Reuse the Scheduler calendar's pixel/time geometry so the two calendars stay
// visually consistent. This module is the single point of coupling — if the
// geometry is ever lifted to a shared util, only this re-export changes.
export {
  HOUR_HEIGHT,
  DAY_MINUTES,
  SNAP_MINUTES,
  DEFAULT_SCROLL_HOUR,
  HOURS,
  clampMinutes,
  snapMinutes,
  minutesToOffset,
  offsetToMinutes,
  minutesNow,
  formatMinutes,
  formatMinutesRange,
  formatHourLabel,
} from "@/pages/Scheduler/utils/calendar";

// The appointment currently being encoded in the create form, mirrored into
// the calendar as a draggable placeholder block (Google-Calendar style).
export interface DraftSlot {
  date: Date;
  startMin: number;
  endMin: number;
}

// A single appointment flattened for calendar rendering: raw ids/labels plus
// concrete start/end Dates (date + time-of-day combined) for positioning.
export interface CalendarAppointment {
  id: string;
  title?: string | null;
  status?: string | null; // "upcoming" | "finished" | "cancelled"
  appointmentType: string; // "online" | "inperson"
  start: Date;
  end: Date;
  clientName?: string | null;
  avatarUrl?: string | null;
  roomUrl?: string | null;
}

// Minimal structural shape of a grouped-appointments query result, so this
// mapper doesn't depend on the generated fragment types.
type RawAppointment = {
  id: string;
  title?: string | null;
  status?: string | null;
  appointmentType: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  client?: {
    profile?: {
      fullName?: string | null;
      avatarUrl?: string | null;
    } | null;
  } | null;
  meetingDetails?: { roomUrl?: string | null } | null;
};

type RawGroup = {
  date: string;
  appointments?: RawAppointment[] | null;
};

/** Minutes since midnight for a full Date (its time component). */
export const minutesOfDay = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes();

/** Flatten the grouped `appointments` query result into calendar events. */
export const toCalendarAppointments = (
  groups: readonly RawGroup[] | null | undefined
): CalendarAppointment[] => {
  const events: CalendarAppointment[] = [];
  (groups ?? []).forEach((group) => {
    (group.appointments ?? []).forEach((appointment) => {
      events.push({
        id: appointment.id,
        title: appointment.title,
        status: appointment.status,
        appointmentType: appointment.appointmentType,
        start: parseDateAndTime(appointment.startDate, appointment.startTime),
        end: parseDateAndTime(
          appointment.endDate ?? appointment.startDate,
          appointment.endTime
        ),
        clientName: appointment.client?.profile?.fullName,
        avatarUrl: appointment.client?.profile?.avatarUrl,
        roomUrl: appointment.meetingDetails?.roomUrl,
      });
    });
  });
  return events;
};

export type DisplayStatus = "current" | "upcoming" | "finished" | "cancelled";

/**
 * The status used for colouring a block. Mirrors the list view: a cancelled or
 * finished appointment keeps that status; an upcoming one that is happening
 * right now shows as "current", and one whose end has passed is "finished".
 */
export const displayStatus = (
  appointment: CalendarAppointment,
  now: Date
): DisplayStatus => {
  if (appointment.status === "cancelled") return "cancelled";
  if (appointment.status === "finished") return "finished";
  if (isAfter(now, appointment.end)) return "finished";
  if (isWithinInterval(now, { start: appointment.start, end: appointment.end }))
    return "current";
  return "upcoming";
};

export interface StatusPalette {
  bg: string;
  border: string;
  text: string;
  dot: string;
}

// Colours echo the list view's status dots (StatusDotIndicator / AppointmentListItem).
export const STATUS_PALETTE: Record<DisplayStatus, StatusPalette> = {
  current: {
    bg: "#FFF6E3",
    border: "#FFD98A",
    text: "#7A5A12",
    dot: "#FFD98A",
  },
  upcoming: {
    bg: "#E8F3FA",
    border: "#2D87BB",
    text: "#155273",
    dot: "#2D87BB",
  },
  finished: {
    bg: "#EEF1F5",
    border: "#B8BEC9",
    text: "#5B6472",
    dot: "#B8BEC9",
  },
  cancelled: {
    bg: "#FDECEC",
    border: "#FF6262",
    text: "#8A2B2B",
    dot: "#FF6262",
  },
};
